import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLaunchIntercomMessenger } from '@/data/intercom/intercom';
import {
    confirmKeycloakPhoneNumberChannel,
    formatNorthAmericanPhoneNumber,
} from '@/feature/account-details/utils/utils';
import { showPhoneNumberEditErrorModal } from '@/feature/account-details/utils/utils';
import { toastPositions } from '@/feature/alerts/components/Toast';
import { useAlerts } from '@/feature/alerts/hooks/use-alerts';
import { LOG_TAG_ACCOUNT_DETAILS_UPDATE } from '@/feature/verify-phone-number/utils/constants';
import { useRootNavigation } from '@/hooks/use-root-navigation';
import { useUserStore } from '@/hooks/use-user-store';
import { useVerificationCode } from '@/hooks/use-verification-code';
import { resetToAccountScreen } from '@/navigation/navigation';
import { AccountEditStackScreenProps } from '@/navigation/types';
import { logger } from '@/utils/logging';

import VerificationScreenTemplate from './VerificationScreenTemplate';

type PhoneNumberEditUpdatedVerificationProps = AccountEditStackScreenProps<'PhoneNumberEditUpdatedVerification'>;

export const PhoneNumberEditUpdatedVerification = ({ route }: PhoneNumberEditUpdatedVerificationProps) => {
    const [isResendRequested, setIsResendRequested] = useState(false);
    const rootNavigation = useRootNavigation();
    const { t } = useTranslation(['account', 'common']);
    const { showToast } = useAlerts();
    const { launchIntercomMessenger } = useLaunchIntercomMessenger();
    const formattedPhoneNumber = formatNorthAmericanPhoneNumber(route.params?.phoneNumber || '');

    const { verificationCode, isSubmitting, errorMessage, onInputChange, setExternalError, setErrorMessage } =
        useVerificationCode(async () => {
            setErrorMessage('');

            try {
                await confirmKeycloakPhoneNumberChannel();
            } catch (err) {
                logger.error(LOG_TAG_ACCOUNT_DETAILS_UPDATE, err);
                showPhoneNumberEditErrorModal(rootNavigation, t, launchIntercomMessenger, () =>
                    resetToAccountScreen(rootNavigation, 'AccountDetails')
                );
                return;
            }

            const { actions, session } = useUserStore.getState();
            await actions.refresh(session);

            // Update Zustand user store with the new phone number to ensure state data is consistent and up to date.
            actions.updateProfile({ mobile_number: route.params?.phoneNumber || '' });

            resetToAccountScreen(rootNavigation, 'AccountDetails');
            showToast({
                message: t('edit_phone_number_success_description', { phoneNumber: formattedPhoneNumber }),
                position: toastPositions.TOP,
            });
        });

    return (
        <VerificationScreenTemplate
            title={t('verify_your_new_number')}
            isLoading={isSubmitting}
            errorMessage={errorMessage}
            verificationCode={verificationCode}
            isResendRequested={isResendRequested}
            onResendPress={() => {
                setIsResendRequested(true);
                setErrorMessage('');
            }}
            setExternalError={setExternalError}
            onInputChange={onInputChange}
            phoneNumberToDisplay={formattedPhoneNumber}
        />
    );
};
